import esProxyClient from "../api/elasticSearchAPI";
import {
  BENEFITS,
  BLOGS,
  BRANCHES,
  BRANDS,
  EMPLOYERS,
  RECIPES,
  RESTAURANTS,
  VF_ENTITIES_TYPE,
} from "../resources/config";
import {
  clearTestConfigFromStorage,
  saveTestConfigEnabledToStorage,
  saveTestConfigToStorage,
} from "../utils/elasticSearchStorage";
import { SET_SEARCH_RESULT_REQUESTED, SET_SEARCH_RESULTS } from "./entitiesActions";

export const SET_ELASTICSEARCH_ENABLED = "SET_ELASTICSEARCH_ENABLED";
export const SET_ELASTICSEARCH_TEST_CONFIG = "SET_ELASTICSEARCH_TEST_CONFIG";
export const RESET_ELASTICSEARCH_TEST_CONFIG = "RESET_ELASTICSEARCH_TEST_CONFIG";
export const TOGGLE_ELASTICSEARCH_TEST_CONFIG = "TOGGLE_ELASTICSEARCH_TEST_CONFIG";

export const setTestConfig = (config) => {
  // Save to localStorage
  saveTestConfigToStorage(config);

  return {
    type: SET_ELASTICSEARCH_TEST_CONFIG,
    payload: config,
  };
};

export const setElasticSearchEnabled = (dispatch, enabled) => {
  try {
    dispatch({
      type: SET_ELASTICSEARCH_ENABLED,
      payload: enabled,
    });
  } catch (error) {
    // Handle React DevTools disconnected port errors and similar issues
    if (error.message?.includes("disconnected port") || error.message?.includes("Extension context invalidated")) {
      console.warn("React DevTools connection issue detected, ignoring error:", error.message);
    } else {
      console.error("Error setting ElasticSearch enabled state:", error);
      throw error;
    }
  }
};

export const resetTestConfig = () => {
  // Clear from localStorage
  clearTestConfigFromStorage();

  return {
    type: RESET_ELASTICSEARCH_TEST_CONFIG,
  };
};

export const enableTestConfig = (isEnabled) => {
  // Save enabled state to localStorage
  saveTestConfigEnabledToStorage(isEnabled);

  return {
    type: TOGGLE_ELASTICSEARCH_TEST_CONFIG,
    payload: isEnabled,
  };
};

// restaurants, chains, businesses, business_branches, recipes, companies, blogs, employers
const entityTypeToArrayName = {
  restaurant: "restaurants",
  // business: "businesses",
  "business-branch": "businessBranches",
  benefit: "benefits",
  company: "companies",
  employer: "employers",
  recipe: "recipes",
  blog: "blogs",
};

// from VF_ENTITIES_TYPE to elasticsearch entity_type
const vfEntityTypeToEntityType = {
  [RESTAURANTS]: "restaurant",
  [BRANCHES]: "business-branch",
  [BRANDS]: "company",
  [EMPLOYERS]: "employer",
  [RECIPES]: "recipe",
  [BLOGS]: "blog",
  [BENEFITS]: "benefit",
};

/**
 * Configuration for ElasticSearch aggregations
 * @typedef {Object} AggregationConfig
 * @property {string} field - The field name for the aggregation
 * @property {string} type - Type of aggregation ("entity_count" or "category")
 * @property {string} [targetProperty] - Target property in categoriesCount (for category type)
 * @property {string} [entityCategoryKey] - Key in entityCategories to find the full category data
 */

/**
 * @type {AggregationConfig[]}
 */
const AGGREGATION_CONFIG = [
  {
    field: "entity_type",
    type: "entity_count",
  },
  {
    field: "rest_categories.id",
    type: "category",
    targetProperty: "restaurants",
    entityCategoryKey: "restaurants",
  },
  {
    field: "business.business_subcats.id",
    type: "category",
    targetProperty: "businessesSubs",
    entityCategoryKey: "businessesSubs",
  },
  {
    field: "recipe_categories",
    type: "category",
    targetProperty: "recipes",
    entityCategoryKey: "recipes",
  },
  // Benefits are a combination of restaurant and business categories
  // {
  //   field: "benefit_categories.id",
  //   type: "category",
  //   targetProperty: "benefits",
  //   entityCategoryKey: "benefits"
  // },
  {
    field: "prod_business_sub",
    type: "category",
    targetProperty: "companiesSubs",
    entityCategoryKey: "companiesSubs",
  },
  {
    field: "blog_categories.id",
    type: "category",
    targetProperty: "blogs",
    entityCategoryKey: "blogs",
  },
  {
    field: "employer_categories",
    type: "category",
    targetProperty: "employers",
    entityCategoryKey: "employers",
  },
];

const COMPUTED_TAGS_AGGREGATIONS = [
  {
    computedTag: "benefit",
    targetProperty: "benefit",
  },
  {
    computedTag: "openNow",
    targetProperty: "openNow",
  },
  {
    computedTag: "vegan",
    targetProperty: "vegan",
  },
  {
    computedTag: "delivery",
    targetProperty: "delivery",
  },
  {
    computedTag: "kosher",
    targetProperty: "kosher",
  },
  {
    computedTag: "certified",
    targetProperty: "certified",
  },
  {
    computedTag: "glutenFree",
    targetProperty: "glutenFree",
  },
  {
    computedTag: "restaurantBenefit",
    targetProperty: "restaurantBenefit",
  },
  {
    computedTag: "businessBenefit",
    targetProperty: "businessBenefit",
  },
];
/**
 * Gets aggregation configurations filtered by type
 * @param {string} type - The aggregation type to filter by
 * @returns {AggregationConfig[]} Filtered aggregation configurations
 */
function getAggregationConfigsByType(type) {
  return AGGREGATION_CONFIG.filter((config) => config.type === type);
}

export const getSearchResultsElastic = async (
  dispatch,
  location,
  searchParams,
  entityCategories,
  pageSize,
  testConfig
) => {
  dispatch({
    type: SET_SEARCH_RESULT_REQUESTED,
    payload: true,
  });

  const { enteredInput, selectedTags, selectedCategories, selectedChainId, selectedBusinessId, currentPage } =
    searchParams;

  /**
   * @typedef {import("@vegan-friendly/es-proxy-types").QueryParams }  QueryParams
   * @type {QueryParams}
   */
  const elasticRequest = {
    searchTerm: enteredInput,
    pagesize: pageSize,
    from: (currentPage - 1) * pageSize,
    location,
    aggregations: AGGREGATION_CONFIG.map((config) => ({
      field: config.field,
    })),
    computedTagAggregations: COMPUTED_TAGS_AGGREGATIONS.reduce((acc, tag) => {
      acc[tag.computedTag] = true;
      return acc;
    }, {}),
  };

  //TODO: tags are not trivial - some of them are calculated, like "open_now". in any case these are not the same tags saved in DB.
  //      So for now we rely on the existing mechanism - filter in client-side
  if (selectedTags?.length > 0) {
    elasticRequest.computedTags = selectedTags.reduce((acc, tag) => {
      acc[tag.name] = true;
      return acc;
    }, {});
  }
  if (selectedCategories?.length > 0) {
    elasticRequest.rest_categories = selectedCategories.map((cat) => cat.id);
  }
  if (selectedChainId) {
    elasticRequest.chainId = selectedChainId;
  }
  if (selectedBusinessId) {
    elasticRequest.businessId = selectedBusinessId;
  }

  if (testConfig) {
    elasticRequest.testConfig = testConfig;
    elasticRequest.testConfig.logQuery = true;
  }

  const { result, success, error } = await esProxyClient.performSearch(elasticRequest);

  let searchResults = {
    success,
    input: {
      searchParams,
      location,
      testConfig,
      pageSize,
    },
    // categoriesCount: {}
  };
  Object.values(entityTypeToArrayName).forEach((arrayName) => {
    searchResults[arrayName] = [];
  });
  if (success) {
    for (const hit of result.hits.hits) {
      const _source = hit._source;
      const fields = hit.fields || {};
      if (Array.isArray(fields.distance) && fields.distance.length > 0) {
        _source.distance = fields.distance[0]; //TODO: do this here?
      }

      // Add score and explanation when testConfig.explain is enabled
      if (testConfig?.explain) {
        _source._score = hit._score;
        _source._explanation = hit._explanation;
      }

      const arrayName = entityTypeToArrayName[_source.entity_type];
      if (arrayName) {
        searchResults[arrayName].push(_source);
      } else {
        console.warn(`Unknown entity type '${_source.entity_type}'. _source:`, _source);
      }
    }

    //This is an ugly hack -
    // the old search mechanism expected businesses with branches inside them, with minimal data. It then flattened the branches.
    // ES returns the branches directly.
    // To be compatible, I'm creating the businesses, and down the line they will be flattened back to branches.
    searchResults.businesses = calcBusinessesFromBranches(searchResults.businessBranches);

    searchResults.categoriesCount = categoriesCountFromElasticResult(result, entityCategories);
    searchResults.computedTagsCounts = getComputedTagsCountsFromElasticResult(result);
    searchResults.counts = getCountsFromElasticResult(result);

    searchResults.total = result.hits.total.value;
    searchResults.pages = Math.ceil(searchResults.total / pageSize);
    //todo: populate business_branches ?
  } else {
    console.error("ElasticSearch error", error);
  }

  dispatch({
    type: SET_SEARCH_RESULTS,
    payload: searchResults,
  });
  dispatch({
    type: SET_SEARCH_RESULT_REQUESTED,
    payload: false,
  });
};

/**
 * Initial structure for categories count results
 */
const INITIAL_CATEGORIES_COUNT = {
  businessesSubs: [],
  restaurants: [],
  recipes: [],
  benefits: [],
  companiesSubs: [],
  blogs: [],
  employers: [],
};

/**
 * Processes a single category aggregation and returns formatted categories with counts
 * @param {Object} aggregation - The aggregation result from ElasticSearch
 * @param {Array} availableCategories - Array of available categories to match against
 * @returns {Array} Array of categories with their counts
 */
function processCategoryAggregation(aggregation, availableCategories) {
  if (!aggregation?.buckets) {
    return [];
  }

  return aggregation.buckets
    .map((categoryAgg) => {
      // eslint-disable-next-line eqeqeq
      const categoryFull = availableCategories.find((cat) => cat.id == categoryAgg.key);
      return {
        id: categoryAgg.key, //this is a fallback, in case categoryFull is undefined
        ...categoryFull,
        count: categoryAgg.doc_count,
      };
    })
    .sort(sortByCategoryName);
}

const sortByCategoryName = (a, b) => a.category_name?.localeCompare(b.category_name) || 0;

function categoriesCountFromElasticResult(esResult, entityCategories) {
  if (!esResult.aggregations) {
    return null;
  }

  const categoriesCount = { ...INITIAL_CATEGORIES_COUNT };

  // Process category aggregations using configuration
  const categoryConfigs = getAggregationConfigsByType("category");

  categoryConfigs.forEach((config) => {
    const aggregation = esResult.aggregations?.[config.field];
    const availableCategories = entityCategories[config.entityCategoryKey];

    if (aggregation && availableCategories) {
      categoriesCount[config.targetProperty] = processCategoryAggregation(aggregation, availableCategories);
    }
  });

  categoriesCount.benefits = [
    ...categoriesCount.businessesSubs.map((c) => ({ ...c, id: "R" + c.id })),
    ...categoriesCount.restaurants.map((c) => ({ ...c, id: "B" + c.id })),
  ].sort(sortByCategoryName);

  return categoriesCount;
}

function getComputedTagsCountsFromElasticResult(esResult) {
  if (!esResult.aggregations) {
    return {};
  }

  const computedTagsCounts = {};
  for (const config of COMPUTED_TAGS_AGGREGATIONS) {
    const doc_count = esResult.aggregations?.[config.computedTag]?.doc_count;
    if (doc_count != null) {
      computedTagsCounts[config.targetProperty] = doc_count;
    }
  }
  return computedTagsCounts;
}

function getCountsFromElasticResult(esResult) {
  // Find the entity_type aggregation configuration
  const entityTypeConfigs = getAggregationConfigsByType("entity_count");
  const entityTypeConfig = entityTypeConfigs[0]; // Should only be one

  if (!entityTypeConfig) {
    return {};
  }

  const buckets = esResult.aggregations?.[entityTypeConfig.field]?.buckets ?? [];
  const esEntityCounts = {};
  for (const bucket of buckets) {
    esEntityCounts[bucket.key] = bucket.doc_count;
  }
  const counts = VF_ENTITIES_TYPE.reduce((acc, entityType) => {
    const esName = vfEntityTypeToEntityType[entityType.name];
    acc[entityType.name] = esEntityCounts[esName] || 0;
    return acc;
  }, {});

  return counts;
}

function calcBusinessesFromBranches(businessBranches) {
  const businesses = [];
  const businessMap = new Map();
  for (const branch of businessBranches) {
    if (!businessMap.has(branch.businessId)) {
      const business = {
        id: branch.businessId,
        name: branch.name,
        description: branch.description,
        search_names: branch.search_names, //undefined
        logo: branch.logo,
        tags: branch.tags,
        business_subcats: branch.business_subcats,
        business_categories: branch.business_categories,
        discount_list: branch.discount_list,
        has_delivery: branch.has_delivery,
        in_online: branch.in_online,
        schedule: branch.schedule,
        branches: [],
        _score: branch._score,
        _explanation: branch._explanation,
      };

      businessMap.set(branch.businessId, business);
      businesses.push(business);
    }
    const business = businessMap.get(branch.businessId);
    business.branches.push(branch);
  }
  return businesses;
}
